<template>
  <div class="carousel-container" v-if="isDesktop">
    <div class="carousel">
      <!-- Previous Button -->
      <button id="prev" v-if="currentIndex > 0" @click="moveCarousel('prev')">
        <!-- SVG for Previous Arrow -->
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            opacity="0.85"
            d="M20 40C31.0457 40 40 31.0457 40 20C40 8.9543 31.0457 0 20 0C8.9543 0 0 8.9543 0 20C0 31.0457 8.9543 40 20 40Z"
            fill="#F6F6F6"
          />
          <path
            d="M22.8789 19.0511L16.1041 25.3465L17.1202 26.2905L23.895 19.9951L22.8789 19.0511Z"
            fill="black"
            stroke="black"
            stroke-width="0.5"
          />
          <path
            d="M23.8948 20.0053L17.12 13.71L16.1046 14.6547L22.8787 20.9493L23.8948 20.0053Z"
            fill="black"
            stroke="black"
            stroke-width="0.5"
          />
        </svg>
      </button>

      <!-- Carousel Images -->
      <div class="carousel-images" ref="carouselImages">
        <div
          v-for="(image, index) in productImages"
          :key="image.src"
          class="carousel-item"
          :class="{
            visible:
              index >= currentIndex && index < currentIndex + visibleCount,
          }"
        >
          <img
            v-if="image.src"
            :src="image.src"
            :fetchpriority="isPriorityImage(index) ? 'high' : undefined"
            :loading="!isPriorityImage(index) ? 'lazy' : undefined"
            @click="selectImage(image)"
            :class="{ active: selectedImage === image }"
            :alt="image.alt || `Carousel Image ${index}`"
          />
        </div>
      </div>

      <!-- Next Button -->
      <button
        id="next"
        v-if="currentIndex + visibleCount < productImages.length"
        @click="moveCarousel('next')"
      >
        <!-- SVG for Next Arrow -->
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            opacity="0.85"
            d="M20 40C31.0457 40 40 31.0457 40 20C40 8.9543 31.0457 0 20 0C8.9543 0 0 8.9543 0 20C0 31.0457 8.9543 40 20 40Z"
            fill="#F6F6F6"
          />
          <path
            d="M22.8789 19.0511L16.1041 25.3465L17.1202 26.2905L23.895 19.9951L22.8789 19.0511Z"
            fill="black"
            stroke="black"
            stroke-width="0.5"
          />
          <path
            d="M23.8948 20.0053L17.12 13.71L16.1046 14.6547L22.8787 20.9493L23.8948 20.0053Z"
            fill="black"
            stroke="black"
            stroke-width="0.5"
          />
        </svg>
      </button>
    </div>
  </div>
</template>


<script>
import { eventBus } from 'EventBus/';
export default {
  name: 'vch-image-carousel',
  props: {
    images: {
      type: Array,
      required: true,
      validator: (value) => Array.isArray(value) && value.length <= 12,
    },
  },
  data() {
    return {
      currentIndex: 0,
      visibleCount: 4,
      productImages: [],
      selectedImage: this.images[0] || '',
      isDesktop: false,
      rtlEnabled: false,
    };
  },
  computed: {
    visibleImages() {
      return this.productImages.slice(
        this.currentIndex,
        this.currentIndex + this.visibleCount
      );
    },
    firstValidImageIndex() {
      return this.productImages.findIndex((image) => image.src);
    },
  },
  mounted() {
    this.rtlEnabled = document.body.classList.contains('rtl-content');
    if (this.images.length > 0) {
      this.productImages = this.images;
    }
    eventBus.$on('update-product-images', this.updateMedia);
    this.checkDesktopView();
    window.addEventListener('resize', this.checkDesktopView);
  },
  beforeDestroy() {
    eventBus.$off('update-product-images');
  },
  methods: {
    updateMedia(updatedImages) {
      this.productImages = updatedImages;
      this.currentIndex = 0;
      this.moveCarousel(true);
      this.selectedImage = this.productImages[0] || '';
    },

    checkDesktopView() {
      this.isDesktop = window.innerWidth >= 1024;
    },

    moveCarousel(direction) {
      const carouselImages = this.$refs.carouselImages;

      const transitionDuration = 500;
      carouselImages.style.transition = `transform ${transitionDuration}ms ease-in-out`;

      if (direction === 'next') {
        if (this.currentIndex + this.visibleCount < this.productImages.length) {
          this.currentIndex++;
        }
      } else if (direction === 'prev') {
        if (this.currentIndex > 0) {
          this.currentIndex--;
        }
      }

      const offset = -(
        this.currentIndex *
        (carouselImages.children[0].offsetWidth + 20)
      );
      if (this.rtlEnabled) {
        carouselImages.style.transform = `translateX(${-offset}px)`;
      } else {
        carouselImages.style.transform = `translateX(${offset}px)`;
      }
    },
    playMainVideo(videoEl) {
      if (!videoEl) return;

      videoEl.muted = true;
      videoEl.defaultMuted = true;
      videoEl.playsInline = true;
      videoEl.setAttribute('muted', '');
      videoEl.setAttribute('playsinline', '');
      videoEl.setAttribute('webkit-playsinline', '');

      const attemptPlay = () => {
        const playPromise = videoEl.play();
        if (playPromise?.catch) {
          playPromise.catch(() => {});
        }
      };

      attemptPlay();
      videoEl.addEventListener('canplay', attemptPlay, { once: true });
    },

    selectImage(image) {
      this.selectedImage = image;
      const mainImage = document.getElementById('main-image');
      const mainVideoEl = document.querySelector('#main-video');
      const mainVideoSource = mainVideoEl?.querySelector('source');
      const mainPicture = document.querySelector('#main-picture');

      if (mainImage && !image.videoSrc) {
        this.removeSourcesFromPicture();
        if (mainVideoEl) {
          mainVideoEl.pause();
          mainVideoEl.style.display = 'none';
        }
        if (mainPicture) mainPicture.style.display = 'block';
        mainImage.src = image.src;
        this.updateHeroPicture(image.src);
      } else if (mainVideoEl && image.videoSrc) {
        if (mainPicture) mainPicture.style.display = 'none';
        mainVideoEl.style.display = 'block';
        if (mainVideoSource) {
          mainVideoSource.src = image.videoSrc;
        }
        mainVideoEl.load();
        this.playMainVideo(mainVideoEl);
        if (image.Mp4VideoTagging) {
          window.dataLayer.push(JSON.parse(image.Mp4VideoTagging));
        }
      }
    },

    removeSourcesFromPicture(pictureSelector = 'picture') {
      const picture = document.querySelector(pictureSelector);
      if (!picture) return;

      const sources = picture.querySelectorAll('source');
      if (sources.length > 0) {
        sources.forEach((source) => source.remove());
      }
    },

    isPriorityImage(index) {
      return this.firstValidImageIndex === index;
    },
    updateHeroPicture(clickedimage) {
      const wrapper = document.querySelector(
        '.vch-media-gallery__item-img-wrapper.primary-image'
      );
      if (!wrapper) return;
      const picture = wrapper.querySelector('picture');
      if (!picture) return;
      picture.querySelectorAll('source').forEach((source) => {
        source.srcset = clickedimage;
      });
    },
  },
};
</script>
